import { Result } from 'neverthrow';

import { ApiError } from 'types/api/ApiError';

const MAX_CONSECUTIVE_FAILURES = 3;

export type JobPollOutcome<TJob> =
  | { type: 'completed'; job: TJob }
  | { type: 'cancelled' }
  | { type: 'not-found'; job: TJob }
  | { type: 'error'; error: ApiError; job: TJob };

export type JobPollOptions<TJob> = {
  signal: AbortSignal;
  getJob: (jobId: string) => Promise<Result<TJob, ApiError>>;
  isActive: (job: TJob) => boolean;
  now?: () => number;
  wait?: (milliseconds: number, signal: AbortSignal) => Promise<boolean>;
  waitUntilVisible?: (signal: AbortSignal) => Promise<boolean>;
  onUpdate?: (job: TJob) => void;
  pollImmediately?: boolean;
};

// Polls a background job until it leaves its active states. Backs off as the job runs
// longer, pauses while the sidebar is hidden, and stops as soon as the signal aborts.
export async function pollJob<TJob extends { jobId: string }>(
  initialJob: TJob,
  {
    signal,
    getJob,
    isActive,
    now = Date.now,
    wait = waitForDelay,
    waitUntilVisible = waitForDocumentVisible,
    onUpdate,
    pollImmediately = false,
  }: JobPollOptions<TJob>
): Promise<JobPollOutcome<TJob>> {
  let job = initialJob;
  let consecutiveFailures = 0;
  let isFirstRequest = true;
  const startedAt = now();

  while (isActive(job)) {
    if (signal.aborted) {
      return { type: 'cancelled' };
    }
    if (!(await waitUntilVisible(signal))) {
      return { type: 'cancelled' };
    }

    if (!isFirstRequest || !pollImmediately) {
      const elapsed = now() - startedAt;
      if (!(await wait(pollDelayForElapsed(elapsed), signal))) {
        return { type: 'cancelled' };
      }
      if (!(await waitUntilVisible(signal))) {
        return { type: 'cancelled' };
      }
    }
    isFirstRequest = false;

    const result = await getJob(job.jobId);
    if (signal.aborted) {
      return { type: 'cancelled' };
    }
    if (result.isErr()) {
      if (result.error.status === 404) {
        return { type: 'not-found', job };
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        return { type: 'error', error: result.error, job };
      }
      continue;
    }

    consecutiveFailures = 0;
    job = result.value;
    onUpdate?.(job);
  }

  return { type: 'completed', job };
}

function pollDelayForElapsed(elapsed: number): number {
  if (elapsed < 10_000) {
    return 2_000;
  }
  if (elapsed < 60_000) {
    return 5_000;
  }
  return 10_000;
}

function waitForDelay(
  milliseconds: number,
  signal: AbortSignal
): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve(false);
      return;
    }
    const timer = globalThis.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve(true);
    }, milliseconds);
    const onAbort = () => {
      globalThis.clearTimeout(timer);
      resolve(false);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function waitForDocumentVisible(signal: AbortSignal): Promise<boolean> {
  if (typeof document === 'undefined' || !document.hidden) {
    return Promise.resolve(!signal.aborted);
  }

  return new Promise((resolve) => {
    const finish = (visible: boolean) => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      signal.removeEventListener('abort', onAbort);
      resolve(visible);
    };
    const onVisibilityChange = () => {
      if (!document.hidden) {
        finish(true);
      }
    };
    const onAbort = () => finish(false);

    document.addEventListener('visibilitychange', onVisibilityChange);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
